import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import ImageWithFallback from '../../components/ImageWithFallback';
import Spinner from '../../components/Spinner';
import AvatarFallback from '../../components/AvatarFallback/AvatarFallback';
import RejectionReasonModal from '../../components/RejectionReasonModal';
import GlassCapsulePopup from '../../components/GlassCapsulePopup';
import BoostModal from '../../components/BoostModal/BoostModal';
import instagramIcon from '../../images/Instagram_icon.png';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { getUniversityLogo as getLocalUniversityLogo } from '../../utils/universityLogos';
import { SUPPORTED_CITIES } from '../../config/cities';
import './Profile.css';

interface University {
    id: number;
    name: string;
    abbreviation: string;
    location: string;
    logo_url: string;
}

interface UserProfile {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    university?: string;
    university_id?: number;
    university_abbreviation?: string;
    avatar_url?: string;
    instagram?: string;
    created_at: string;
    // Email verification status
    email_verification_status: 'unverified' | 'verified';
    student_verification_status?: 'unverified' | 'pending' | 'approved' | 'rejected';
    student_id_url?: string;
}


interface UserListing {
    id: string;
    title: string;
    description: string;
    monthly_rent: number;
    address: string;
    unit_type: string;
    is_available: boolean;
    university_name: string;
    university_id: number;
    lease_term: string;
    parking: string;
    allow_pets: string;
    gender_preference: string;
    pictures: string[];
    verification_status: string;
    rejection_reason?: string;
    created_at: string;
    is_boosted?: boolean;
    boost_expiration?: string;
}

const Profile: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, logout, isAuthenticated, token, isLoading: authLoading, refreshUserProfile, updateProfile } = useAuth();
    const [profile, setProfile] = useState<UserProfile | null>((user as unknown as UserProfile) || null);
    const [listings, setListings] = useState<UserListing[]>([]);
    const [listingsLoading, setListingsLoading] = useState(true);
    const [universities, setUniversities] = useState<University[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(!user);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [avatarRetryCount, setAvatarRetryCount] = useState(0);
    const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);
    const [isUploadingStudentId, setIsUploadingStudentId] = useState(false);
    const [studentIdUploadError, setStudentIdUploadError] = useState<string | null>(null);
    const [showStudentIdSuccess, setShowStudentIdSuccess] = useState(false);
    const [showStudentVerifyModal, setShowStudentVerifyModal] = useState(false);
    const [selectedUniversityId, setSelectedUniversityId] = useState<string>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [listingToDelete, setListingToDelete] = useState<UserListing | null>(null);
    const [isDeletingListing, setIsDeletingListing] = useState(false);
    const [gotItThroughUsublease, setGotItThroughUsublease] = useState<boolean | null>(null);
    const [deleteFeedback, setDeleteFeedback] = useState('');
    const [lastErrorDetails, setLastErrorDetails] = useState<string | null>(null);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState<string>('');
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const [showVerificationSuccess, setShowVerificationSuccess] = useState(false);
    const [showAvatarSuccess, setShowAvatarSuccess] = useState(false);
    const [showProfileUpdateSuccess, setShowProfileUpdateSuccess] = useState(false);
    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        university_id: '',
        email: ''
    });
    const [editProfileView, setEditProfileView] = useState<'edit' | 'changePassword'>('edit');
    const [changePasswordData, setChangePasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
    const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
    const [boostListingId, setBoostListingId] = useState<string>('');
    const [showBoostSuccess, setShowBoostSuccess] = useState(false);
    const [boostTimeRemaining, setBoostTimeRemaining] = useState<string | null>(null);
    const [showBoostTimePopup, setShowBoostTimePopup] = useState(false);

    const fetchUniversities = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.UNIVERSITIES.LIST);
            setUniversities(response.data.data || []);
        } catch (error) {
            console.error('Error fetching universities:', error);
        }
    };

    const showRejectionReason = (reason: string) => {
        setRejectionReason(reason);
        setShowRejectionModal(true);
    };

    const closeRejectionModal = () => {
        setShowRejectionModal(false);
        setRejectionReason('');
    };


    const handleDeleteListing = (listing: UserListing) => {
        setListingToDelete(listing);
        setIsDeleteModalOpen(true);
        setGotItThroughUsublease(null);
        setDeleteFeedback('');
    };

    const confirmDeleteListing = async () => {
        if (!listingToDelete) return;

        setIsDeletingListing(true);
        setError(null);

        try {
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) {
                setError('No authentication token found. Please log in again.');
                return;
            }

            // Submit feedback if provided
            if (gotItThroughUsublease !== null || deleteFeedback.trim()) {
                try {
                    const feedbackMessage = `Listing Deletion Feedback\n\nDid you find your sublessee through USublease? ${gotItThroughUsublease === true ? 'Yes' : gotItThroughUsublease === false ? 'No' : 'Not answered'}\n\nFeedback: ${deleteFeedback.trim() || 'No feedback provided'}`;

                    await fetch(API_ENDPOINTS.FEEDBACK, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            email: profile?.email || user?.email || 'unknown@example.com',
                            message: feedbackMessage
                        })
                    });
                } catch (feedbackError) {
                    console.error('Error submitting feedback:', feedbackError);
                    // Don't block deletion if feedback fails
                }
            }

            const response = await fetch(`${API_BASE_URL}/api/v1/subleases/${listingToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                // Close the delete modal
                setIsDeleteModalOpen(false);
                setListingToDelete(null);
                setGotItThroughUsublease(null);
                setDeleteFeedback('');
                // Refresh listings
                fetchUserListings();
                // Refresh user profile to update has_listings status (for Boost button in NavBar)
                if (refreshUserProfile) {
                    refreshUserProfile();
                }
                // Show success popup
                setShowDeleteSuccess(true);
            } else if (response.status === 401) {
                setError('Your session has expired. Please log in again.');
                logout();
                navigate('/');
            } else if (response.status === 403) {
                setError('You are not authorized to delete this listing.');
            } else if (response.status === 404) {
                setError('Listing not found. It may have already been deleted.');
                // Still refresh listings in case it was deleted elsewhere
                fetchUserListings();
            } else {
                const errorData = await response.json().catch(() => ({}));
                setError(errorData.detail || 'Failed to delete listing. Please try again.');
            }
        } catch (err) {
            console.error('Delete listing error:', err);
            setError('Network error occurred while deleting listing.');
        } finally {
            setIsDeletingListing(false);
        }
    };

    const cancelDeleteListing = () => {
        setIsDeleteModalOpen(false);
        setListingToDelete(null);
        setGotItThroughUsublease(null);
        setDeleteFeedback('');
    };


    const compressAvatarImage = (file: File, maxWidth: number = 800, maxHeight: number = 800, quality: number = 0.8): Promise<File> => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            let objectUrl: string | null = null;

            img.onload = () => {
                try {
                    // Calculate new dimensions (max 400x400 for avatars)
                    let { width, height } = img;

                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width *= ratio;
                        height *= ratio;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    // Draw and compress
                    ctx?.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        // Clean up object URL
                        if (objectUrl) {
                            URL.revokeObjectURL(objectUrl);
                        }

                        if (blob) {
                            const compressedFile = new File([blob], file.name, {
                                type: 'image/jpeg',
                                lastModified: Date.now()
                            });
                            console.log(`Avatar compressed: ${file.size} bytes -> ${compressedFile.size} bytes (${(compressedFile.size / file.size * 100).toFixed(1)}%)`);
                            resolve(compressedFile);
                        } else {
                            resolve(file);
                        }
                    }, 'image/jpeg', quality);
                } catch (error) {
                    // Clean up object URL on error
                    if (objectUrl) {
                        URL.revokeObjectURL(objectUrl);
                    }
                    console.error('Error compressing avatar:', error);
                    resolve(file); // Fallback to original file
                }
            };

            img.onerror = () => {
                // Clean up object URL on error
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
                console.error('Error loading image for compression');
                resolve(file); // Fallback to original file
            };

            objectUrl = URL.createObjectURL(file);
            img.src = objectUrl;
        });
    };

    const triggerStudentIdUpload = (source: 'gallery' | 'camera' = 'gallery') => {
        const inputId = source === 'camera' ? 'student-id-camera-input' : 'student-id-upload-input';
        document.getElementById(inputId)?.click();
    };

    const handleStudentIdUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 10MB to match backend)
        if (file.size > 10 * 1024 * 1024) {
            setError('File size must be less than 10MB');
            return;
        }

        setIsUploadingStudentId(true);
        setError(null);
        setStudentIdUploadError(null);

        try {
            // Compress image using established Canvas helper but with higher resolution (1200x1200) for legibility
            const compressedFile = await compressAvatarImage(file, 1200, 1200, 0.8);
            
            // Read as Base64 data URL
            const base64Data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result as string);
                };
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(compressedFile);
            });

            const authToken = token || localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/api/v1/users/verify-student-base64`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    image_data: base64Data
                })
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Student ID verification request submitted successfully:', result);

                // Small delay to let DB write propagate
                await new Promise(resolve => setTimeout(resolve, 500));

                // Refresh profile data
                await fetchProfile();

                // Refresh Auth context user
                if (refreshUserProfile) {
                    await refreshUserProfile();
                }

                setShowStudentIdSuccess(true);
                setShowStudentVerifyModal(false);
                // Auto-clear success state after a few seconds
                setTimeout(() => setShowStudentIdSuccess(false), 5000);
            } else {
                const errorData = await response.json();
                const errMsg = errorData.detail || errorData.message || 'Failed to submit verification request';
                setStudentIdUploadError(errMsg);
                setError(errMsg);
            }
        } catch (err: any) {
            console.error('Error uploading student ID:', err);
            setStudentIdUploadError('Failed to upload ID. Please try again.');
            setError('Failed to upload ID. Please try again.');
        } finally {
            setIsUploadingStudentId(false);
            // Clear the file input
            event.target.value = '';
        }
    };

    const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 10MB to match backend)
        if (file.size > 10 * 1024 * 1024) {
            setError('File size must be less than 10MB');
            return;
        }

        console.log('File selected for upload:', {
            name: file.name,
            type: file.type,
            size: file.size
        });

        setIsUploadingAvatar(true);
        setError(null);
        setLastErrorDetails(null);

        try {
            // Compress image on client-side before converting to base64
            // This reduces payload size and prevents 413 errors
            // Start with reasonable settings (800x800, 80% quality) for high density / Retina display clarity
            let compressedFile = await compressAvatarImage(file, 800, 800, 0.8);

            // Convert compressed file to base64 to avoid binary data corruption in Lambda
            let base64Data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    resolve(result);
                };
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(compressedFile);
            });

            console.log('File converted to base64, length:', base64Data.length);

            // Check if base64 data is still too large (API Gateway has ~6MB limit for Lambda payloads)
            // Base64 increases size by ~33%, so we want to keep compressed file under ~4.5MB
            let base64SizeMB = base64Data.length / (1024 * 1024);

            // If still too large, try more aggressive compression (500x500, 70% quality)
            if (base64SizeMB > 5) {
                console.log(`Base64 payload still large (${base64SizeMB.toFixed(2)}MB), trying more aggressive compression...`);
                compressedFile = await compressAvatarImage(file, 500, 500, 0.7);

                base64Data = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const result = reader.result as string;
                        resolve(result);
                    };
                    reader.onerror = () => reject(reader.error);
                    reader.readAsDataURL(compressedFile);
                });

                base64SizeMB = base64Data.length / (1024 * 1024);
                console.log(`Re-compressed to ${base64SizeMB.toFixed(2)}MB`);
            }

            // Final check - if still too large after aggressive compression, show error
            if (base64SizeMB > 6) {
                setError(`Image is still too large after compression (${base64SizeMB.toFixed(1)}MB). Please try a smaller or lower resolution image.`);
                setIsUploadingAvatar(false);
                return;
            }

            const authToken = token || localStorage.getItem('auth_token');
            console.log('Uploading avatar with token:', authToken ? 'present' : 'missing');
            console.log('API endpoint:', `${API_BASE_URL}/api/v1/users/avatar-base64`);
            console.log('Base64 payload size:', `${base64SizeMB.toFixed(2)}MB`);

            const response = await fetch(`${API_BASE_URL}/api/v1/users/avatar-base64`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    image_data: base64Data
                })
            });

            console.log('Upload response status:', response.status);
            console.log('Upload response headers:', Object.fromEntries(response.headers.entries()));
            console.log('Response ok:', response.ok);

            if (response.ok) {
                const result = await response.json();
                console.log('Avatar upload successful:', result);
                console.log('Avatar URL from API:', result.avatar_url);

                // Small delay to ensure database write has propagated (DynamoDB eventual consistency)
                await new Promise(resolve => setTimeout(resolve, 500));

                // Refresh profile from database to ensure we have the latest data
                // This ensures the avatar URL is properly saved in the database
                await fetchProfile();

                // Also refresh the user context to keep global state in sync
                if (refreshUserProfile) {
                    await refreshUserProfile();
                }

                // Update the profile with new avatar URL immediately (fallback if fetchProfile hasn't updated yet)
                setProfile(prev => {
                    if (prev) {
                        const updated = { ...prev, avatar_url: result.avatar_url };
                        console.log('Updated profile with new avatar URL:', updated.avatar_url);
                        console.log('Previous avatar URL was:', prev.avatar_url);
                        setAvatarRetryCount(0); // Reset retry count for new avatar
                        return updated;
                    }
                    return prev;
                });

                // Also update the user context immediately for global state consistency
                if (user) {
                    console.log('Updating user context with avatar URL:', result.avatar_url);
                    updateProfile({ avatar_url: result.avatar_url });
                }

                // Force re-render to show new avatar immediately (with cache-busting)
                setAvatarRefreshKey(prev => prev + 1);

                // Reset refresh key after a delay to allow caching on subsequent loads
                // This ensures the new avatar displays immediately but subsequent page loads use cache
                setTimeout(() => {
                    console.log('Resetting avatar refresh key for optimal caching...');
                    setAvatarRefreshKey(0);
                }, 10000); // Reset after 10 seconds to allow immediate display but enable caching

                setShowAvatarSuccess(true);

                // Clear the file input
                event.target.value = '';
            } else {
                console.error('Avatar upload failed with status:', response.status);
                console.error('Response headers:', response.headers);

                let errorMessage = 'Failed to upload avatar';
                let errorDetails = '';

                // Handle specific error cases
                if (response.status === 413) {
                    errorMessage = 'Image is too large. Please try a smaller image or compress it before uploading.';
                    errorDetails = 'The image file is too large for upload. Try selecting a smaller image or one with lower resolution.';
                } else {
                    try {
                        const errorData = await response.json();
                        console.error('Error response data:', errorData);
                        errorMessage = errorData.detail || errorData.message || `Server error (${response.status})`;
                        errorDetails = `Status: ${response.status}, Response: ${JSON.stringify(errorData)}`;
                    } catch (parseError) {
                        console.error('Failed to parse error response:', parseError);
                        const errorText = await response.text();
                        console.error('Error response text:', errorText);
                        errorMessage = `Server error (${response.status}): ${errorText}`;
                        errorDetails = `Status: ${response.status}, Text: ${errorText}`;
                    }
                }

                setError(errorMessage);
                setLastErrorDetails(errorDetails);
            }
        } catch (error) {
            console.error('Avatar upload error:', error);
            // Show more specific error information
            let errorMessage = 'Failed to upload avatar. Please try again.';
            let errorDetails = '';
            if (error instanceof Error) {
                errorMessage = `Upload error: ${error.message}`;
                errorDetails = `Error: ${error.message}, Stack: ${error.stack}`;
            } else if (typeof error === 'string') {
                errorMessage = `Upload error: ${error}`;
                errorDetails = `Error: ${error}`;
            } else {
                errorDetails = `Unknown error: ${JSON.stringify(error)}`;
            }
            setError(errorMessage);
            setLastErrorDetails(errorDetails);
        } finally {
            setIsUploadingAvatar(false);
        }
    };

    const fetchProfile = useCallback(async () => {
        try {
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) {
                setError('No authentication token found. Please log in again.');
                return;
            }

            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });


            if (response.ok) {
                const responseData = await response.json();

                const profileData = responseData.data || responseData;

                // If profile data is empty or missing, use user data from auth context as fallback
                if (!profileData || !profileData.email) {
                    if (user) {
                        const fallbackProfile: UserProfile = {
                            id: user.id || '',
                            email: user.email || '',
                            first_name: user.first_name || '',
                            last_name: user.last_name || '',
                            university_id: undefined,
                            avatar_url: user.avatar_url || '',
                            created_at: user.created_at || '',
                            email_verification_status: user.email_verification_status || 'unverified',
                            student_verification_status: user.student_verification_status || 'unverified',
                            student_id_url: user.student_id_url
                        };
                        setProfile(fallbackProfile);
                        setFormData({
                            first_name: fallbackProfile.first_name || '',
                            last_name: fallbackProfile.last_name || '',
                            university_id: fallbackProfile.university_id?.toString() || '',
                            email: fallbackProfile.email || ''
                        });
                    } else {
                        setError('No profile data available');
                    }
                } else {
                    setProfile(profileData);
                    setFormData({
                        first_name: profileData.first_name || '',
                        last_name: profileData.last_name || '',
                        university_id: profileData.university_id?.toString() || '',
                        email: profileData.email || ''
                    });
                }
            } else if (response.status === 401) {
                setError('Your session has expired. Please log in again.');
                logout(); // Clear invalid session
                navigate('/'); // Redirect to home page
            } else {
                const errorText = await response.text();
                setError(`Failed to fetch profile: ${response.status} ${errorText}`);
            }
        } catch (err) {
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    }, [token, user, logout]);


    const fetchUserListings = useCallback(async () => {
        try {
            setListingsLoading(true);
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) return;

            const response = await fetch(`${API_BASE_URL}/api/v1/users/listings?include_unavailable=true`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const listingsData = await response.json();
                // Filter out rejected listings - they shouldn't show on profile
                const filteredListings = (listingsData.data || []).filter(
                    (listing: UserListing) => {
                        const status = (listing.verification_status || '').toLowerCase().trim();
                        return status !== 'rejected';
                    }
                );
                setListings(filteredListings);
            } else if (response.status === 401) {
                logout();
                navigate('/'); // Redirect to home page
            } else {
            }
        } catch (err) {
        } finally {
            setListingsLoading(false);
        }
    }, [token, logout, navigate]);



    // Sync profile and form data from the cached user object
    useEffect(() => {
        if (user) {
            setProfile(user as unknown as UserProfile);
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                university_id: user.university_id?.toString() || '',
                email: user.email || ''
            });
            setLoading(false);
        }
    }, [user]);

    // Fetch fresh profile details and listings on mount
    useEffect(() => {
        // Wait for auth to finish loading before making decisions
        if (authLoading) {
            return;
        }

        if (isAuthenticated && token) {
            fetchProfile();
            fetchUserListings();
            fetchUniversities();
        } else {
            setLoading(false);
            setError('Please log in to view your profile');
            // Redirect to home page after a short delay
            setTimeout(() => {
                navigate('/');
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isAuthenticated, token, navigate]);

    // Debug effect to log profile changes
    useEffect(() => {
        console.log('Profile state changed:', {
            avatar_url: profile?.avatar_url,
            first_name: profile?.first_name,
            last_name: profile?.last_name,
            avatarRefreshKey,
            profileExists: !!profile
        });
    }, [profile?.avatar_url, avatarRefreshKey, profile]);

    // Listen for verification success event
    useEffect(() => {
        const handleVerificationSuccess = () => {
            setShowVerificationSuccess(true);
            refreshUserProfile();
        };

        window.addEventListener('showVerificationSuccess', handleVerificationSuccess);
        return () => {
            window.removeEventListener('showVerificationSuccess', handleVerificationSuccess);
        };
    }, [refreshUserProfile]);

    // Check for verification success
    useEffect(() => {
        const verificationSubmitted = searchParams.get('verification_submitted');
        const boostSuccess = searchParams.get('boost_success');
        const boostCancelled = searchParams.get('boost_cancelled');
        const listingIdForBoost = searchParams.get('listing_id');

        if (verificationSubmitted === 'true') {
            setShowVerificationSuccess(true);
            refreshUserProfile();
            searchParams.delete('verification_submitted');
            setSearchParams(searchParams, { replace: true });
        }

        if (boostSuccess === 'true' && listingIdForBoost) {
            const sessionId = searchParams.get('session_id');

            // Stripe redirects cause a full reload; wait until auth token is available
            // so verify-boost can actually run (otherwise we'd clear params and never retry).
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) return;

            const processBoost = async () => {
                try {
                    let verified = false;

                    // Primary: verify via session_id (fast, definitive)
                    if (sessionId) {
                        await axios.post(
                            `${API_BASE_URL}/api/v1/subleases/verify-boost/${listingIdForBoost}`,
                            { session_id: sessionId },
                            { headers: { 'Authorization': `Bearer ${authToken}` } }
                        );
                        verified = true;
                    }

                    // Secondary: if session_id is missing or webhook is delayed, poll listing state.
                    // This prevents showing "Boost Applied" when nothing was written to DynamoDB.
                    const pollUntilBoosted = async () => {
                        const maxAttempts = 10; // ~20s total
                        for (let attempt = 0; attempt < maxAttempts; attempt++) {
                            try {
                                const r = await fetch(`${API_BASE_URL}/api/v1/subleases/${listingIdForBoost}`);
                                if (r.ok) {
                                    const l = await r.json();
                                    if (l?.is_boosted === true) return true;
                                }
                            } catch {
                                // ignore transient network errors
                            }
                            await new Promise(res => setTimeout(res, 2000));
                        }
                        return false;
                    };

                    const isBoostedNow = verified ? true : await pollUntilBoosted();
                    if (!isBoostedNow) {
                        setError("Payment completed, but boost was not applied yet. Please refresh in a moment, or contact support if it doesn't update.");
                        return;
                    }

                    setShowBoostSuccess(true);
                    refreshUserProfile();
                    fetchUserListings();

                    // After success, show time remaining for that listing
                    setTimeout(() => {
                        handleShowBoostTime(listingIdForBoost);
                    }, 1000);
                } catch (err) {
                    console.error('Error verifying boost:', err);
                    setError("Payment completed, but boost verification failed. Please refresh in a moment; if it still doesn't show boosted, contact support.");
                }
            };

            processBoost();

            // Only clear URL params once we have a token, and after we kicked off verification.
            // (We don't await here to keep UI responsive.)
            searchParams.delete('boost_success');
            searchParams.delete('listing_id');
            searchParams.delete('session_id');
            setSearchParams(searchParams, { replace: true });
        }

        if (boostCancelled === 'true') {
            searchParams.delete('boost_cancelled');
            setSearchParams(searchParams, { replace: true });
        }

        const verifyParam = searchParams.get('verify');
        if (verifyParam === 'true') {
            setStudentIdUploadError(null);
            setShowStudentVerifyModal(true);
            searchParams.delete('verify');
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, token]);

    // Sync university selection with profile when verification modal opens
    useEffect(() => {
        if (showStudentVerifyModal && profile?.university_id && !selectedUniversityId) {
            setSelectedUniversityId(profile.university_id.toString());
        }
    }, [showStudentVerifyModal, profile, selectedUniversityId]);

    const formatTimeRemaining = (expiration: string): string => {
        const now = new Date();
        const exp = new Date(expiration);
        const diffMs = exp.getTime() - now.getTime();

        if (diffMs <= 0) return 'Expired';

        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (diffDays >= 1) return `${diffDays} day${diffDays === 1 ? '' : 's'} left`;
        if (diffHours >= 1) return `${diffHours} hour${diffHours === 1 ? '' : 's'} left`;
        return `${Math.max(diffMins, 1)} minute${diffMins === 1 ? '' : 's'} left`;
    };

    const handleShowBoostTime = (listingId: string) => {
        const listing = listings.find(l => l.id === listingId);
        if (listing && listing.boost_expiration) {
            const time = formatTimeRemaining(listing.boost_expiration);
            setBoostTimeRemaining(time);
            setShowBoostTimePopup(true);
        }
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        try {
            setError(null);
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) {
                setError('No authentication token found. Please log in again.');
                return;
            }

            // Validate email format if email is provided or changed
            if (formData.email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) {
                    setError('Please enter a valid email address');
                    return;
                }
            }

            // Build submit data
            const submitData: any = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                university_id: formData.university_id ? parseInt(formData.university_id) : null
            };

            // Only include email if it's different from current email
            if (formData.email && formData.email !== profile?.email) {
                submitData.email = formData.email;
            }

            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(submitData)
            });

            if (response.ok) {
                const updatedProfile = await response.json();
                setProfile(updatedProfile);
                setIsEditing(false);
                setShowProfileUpdateSuccess(true);
                // Refresh user profile in auth context
                if (refreshUserProfile) {
                    await refreshUserProfile();
                }
            } else if (response.status === 401) {
                setError('Your session has expired. Please log in again.');
                logout();
                navigate('/'); // Redirect to home page
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to update profile');
            }
        } catch (err) {
            setError('Network error occurred');
        }
    };

    const handleCancel = () => {
        if (profile) {
            setFormData({
                first_name: profile.first_name || '',
                last_name: profile.last_name || '',
                university_id: profile.university_id?.toString() || '',
                email: profile.email || ''
            });
        }
        setIsEditing(false);
        setEditProfileView('edit');
        setError(null);
    };

    const handleTooltipClick = (tooltipId: string) => {
        setActiveTooltip(activeTooltip === tooltipId ? null : tooltipId);
    };


    const handleChangePasswordCancel = () => {
        setEditProfileView('edit');
        setChangePasswordData({
            current_password: '',
            new_password: '',
            confirm_password: ''
        });
        setChangePasswordError(null);
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
    };

    const handleChangePassword = async () => {
        try {
            setChangePasswordError(null);
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) {
                setChangePasswordError('No authentication token found. Please log in again.');
                return;
            }

            // Validation
            if (!changePasswordData.current_password) {
                setChangePasswordError('Current password is required');
                return;
            }

            if (!changePasswordData.new_password) {
                setChangePasswordError('New password is required');
                return;
            }

            if (changePasswordData.new_password.length < 6) {
                setChangePasswordError('New password must be at least 6 characters long');
                return;
            }

            if (changePasswordData.new_password !== changePasswordData.confirm_password) {
                setChangePasswordError('New passwords do not match');
                return;
            }

            const submitData = {
                current_password: changePasswordData.current_password,
                new_password: changePasswordData.new_password
            };

            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(submitData)
            });

            if (response.ok) {
                // Clear password fields
                setChangePasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                });
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmPassword(false);
                setEditProfileView('edit');
                setShowProfileUpdateSuccess(true);
                // Refresh user profile in auth context
                if (refreshUserProfile) {
                    await refreshUserProfile();
                }
            } else if (response.status === 401) {
                setChangePasswordError('Your session has expired. Please log in again.');
                logout();
                navigate('/');
            } else {
                const errorData = await response.json();
                setChangePasswordError(errorData.detail || 'Failed to change password');
            }
        } catch (err) {
            setChangePasswordError('Network error occurred');
        }
    };

    const getUniversityName = (): string | null => {
        if (!profile) return null;
        if (profile.university_id === null) return null;

        // If the profile's university string matches a supported city, return that city label
        const isCity = SUPPORTED_CITIES.some(c => c.label === profile.university);
        if (isCity) {
            return profile.university || null;
        }

        if (profile.university_id) {
            const u = universities.find((university) => university.id === profile.university_id);
            if (u?.name) return u.name;
        }

        return profile.university || null;
    };

    const getUniversityAbbreviation = (): string | null => {
        if (!profile) return null;

        // If university_id is explicitly null, the user has deselected their university
        if (profile.university_id === null) return null;

        // Prefer the abbreviation returned directly from the API (no extra fetch needed)
        if (profile.university_abbreviation) return profile.university_abbreviation;

        // Fallback: look up from the universities list (covers legacy profiles
        // where university_abbreviation wasn't yet populated by the backend)
        if (profile.university_id) {
            const u = universities.find((university) => university.id === profile.university_id);
            if (u?.abbreviation) return u.abbreviation;
        }

        if (profile.university) {
            const raw = profile.university.trim();
            if (!raw) return null;
            const byNameOrAbbrev = universities.find((u) => {
                const n = (u.name || '').toLowerCase();
                const a = (u.abbreviation || '').toLowerCase();
                const r = raw.toLowerCase();
                return n === r || a === r;
            });
            return byNameOrAbbrev?.abbreviation || raw;
        }

        return null;
    };

    const getDisplayUniversityName = (): string | null => {
        const name = getUniversityName();
        if (!name) return null;
        if (name.length > 20) {
            const abb = getUniversityAbbreviation();
            if (abb && abb.length < name.length) {
                return abb;
            }
        }
        return name;
    };

    const getUniversityLogoUrl = (): string | null => {
        if (!profile || profile.university_id === null) return null;

        // 1. Try local logo utility first (consistent with Home screen)
        const localLogo = getLocalUniversityLogo(profile.university || '', profile.university_abbreviation);
        if (localLogo) return localLogo;

        // 2. Fallback to DB logo_url
        if (profile.university_id) {
            const u = universities.find((university) => university.id === profile.university_id);
            if (u?.logo_url) return u.logo_url;
        }
        return null;
    };

    if (authLoading || loading) {
        return (
            <div className="profile-route">
                <NavBar inverted={true} />
                <div className="profile-loading-screen">
                    <div className="profile-loading-dots" aria-label="Loading" role="status">
                        <span />
                        <span />
                        <span />
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="profile-route">
                <NavBar inverted={true} />
                <div className="profile-page profile-page--profile">
                    <div className="profile-container">
                        <div className="profile-card">
                            <div className="error">{error || 'Failed to load profile'}</div>
                            <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                                Debug: isAuthenticated={isAuthenticated ? 'true' : 'false'},
                                token={token ? 'present' : 'missing'},
                                user={user ? user.email : 'none'}
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="profile-route">
            <NavBar inverted={true} />
            <div className="profile-page profile-page--profile">
                <div className="profile-container">
                    <div className="profile-card">
                        {/* Profile Header */}
                        <div className="profile-header">
                            <div className="profile-header-content">
                                <div className="profile-avatar-section">
                                    <div className="profile-avatar">
                                        <input
                                            type="file"
                                            id="avatar-upload"
                                            accept="image/*"
                                            onChange={handleAvatarUpload}
                                            style={{ display: 'none' }}
                                            disabled={isUploadingAvatar}
                                        />
                                        <input
                                            type="file"
                                            id="student-id-upload-input"
                                            accept="image/*"
                                            onChange={handleStudentIdUpload}
                                            style={{ display: 'none' }}
                                            disabled={isUploadingStudentId}
                                        />
                                        <input
                                            type="file"
                                            id="student-id-camera-input"
                                            accept="image/*"
                                            capture="environment"
                                            onChange={handleStudentIdUpload}
                                            style={{ display: 'none' }}
                                            disabled={isUploadingStudentId}
                                        />
                                        <label htmlFor="avatar-upload" className="avatar-upload-label">
                                            {profile?.avatar_url ? (
                                                <img
                                                    key={`${profile.avatar_url}-${avatarRefreshKey}`} // Force re-render only when avatar URL or refresh key changes
                                                    src={profile.avatar_url.startsWith('http')
                                                        ? (avatarRefreshKey > 0 ? `${profile.avatar_url}?v=${avatarRefreshKey}` : profile.avatar_url)
                                                        : (avatarRefreshKey > 0 ? `${API_BASE_URL}${profile.avatar_url}?v=${avatarRefreshKey}` : `${API_BASE_URL}${profile.avatar_url}`)}
                                                    alt={`${profile?.first_name} ${profile?.last_name}`}
                                                    onLoadStart={() => {
                                                        if (profile?.avatar_url) {
                                                            const finalSrc = profile.avatar_url.startsWith('http')
                                                                ? (avatarRefreshKey > 0 ? `${profile.avatar_url}?v=${avatarRefreshKey}` : profile.avatar_url)
                                                                : (avatarRefreshKey > 0 ? `${API_BASE_URL}${profile.avatar_url}?v=${avatarRefreshKey}` : `${API_BASE_URL}${profile.avatar_url}`);
                                                            console.log('Avatar image loading started:', {
                                                                profileAvatarUrl: profile.avatar_url,
                                                                finalSrc: finalSrc,
                                                                avatarRefreshKey: avatarRefreshKey
                                                            });
                                                        }
                                                    }}
                                                    onLoad={() => {
                                                        console.log('Avatar image loaded successfully');
                                                        setAvatarRetryCount(0); // Reset retry count on success
                                                        // Show the image and hide placeholder
                                                        const img = document.querySelector('.profile-avatar img') as HTMLElement;
                                                        const placeholder = document.querySelector('.profile-avatar .avatar-placeholder') as HTMLElement;
                                                        if (img) img.style.display = 'block';
                                                        if (placeholder) placeholder.style.display = 'none';
                                                    }}
                                                    onError={(e) => {

                                                        const img = e.currentTarget;

                                                        // Only retry if we haven't exceeded the limit
                                                        if (avatarRetryCount < 3 && profile?.avatar_url) {
                                                            const baseUrl = profile.avatar_url.split('?')[0]; // Remove existing parameters
                                                            // Use refresh key for retry to maintain cache benefits
                                                            const newSrc = avatarRefreshKey > 0
                                                                ? `${baseUrl}?v=${avatarRefreshKey}&retry=${avatarRetryCount + 1}`
                                                                : `${baseUrl}?retry=${avatarRetryCount + 1}`;

                                                            setTimeout(() => {
                                                                setAvatarRetryCount(prev => prev + 1);
                                                                img.src = newSrc;
                                                            }, 1000);
                                                        } else {
                                                        }

                                                        // Hide the image and show placeholder
                                                        img.style.display = 'none';
                                                        const placeholder = img.nextElementSibling as HTMLElement;
                                                        if (placeholder) {
                                                            placeholder.style.display = 'flex';
                                                        }
                                                    }}
                                                />
                                            ) : null}
                                            <AvatarFallback
                                                firstName={profile?.first_name}
                                                lastName={profile?.last_name}
                                                email={profile?.email}
                                                size={230}
                                                className="avatar-placeholder"
                                                style={{
                                                    display: profile?.avatar_url ? 'none' : 'flex',
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '16px',
                                                    fontSize: '2.5rem',
                                                    backgroundColor: '#f3f4f6',
                                                    background: '#f3f4f6',
                                                    color: '#111827'
                                                }}
                                            />
                                            <div className={`avatar-overlay ${isUploadingAvatar ? 'uploading' : ''}`}>
                                                {isUploadingAvatar ? (
                                                    <Spinner size="small" color="green" />
                                                ) : (
                                                    <div className="pencil-icon">✏</div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="profile-info">
                                    <div className="profile-name-section">
                                        <div className="profile-name-info">
                                            <div className="profile-name-with-badge">
                                                <h1 className="profile-name">
                                                    {profile?.first_name
                                                        ? `${profile.first_name}${profile?.last_name ? ` ${profile.last_name}` : ''}`.trim()
                                                        : 'User'
                                                    }
                                                </h1>
                                            </div>                                            {/* Profile Details Tile */}
                                            <div className="profile-details-tile">
                                                {/* Instagram Row */}
                                                {profile?.instagram ? (
                                                    <a
                                                        href={`https://instagram.com/${profile.instagram}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="profile-tile-row interactive"
                                                    >
                                                        <svg
                                                            className="profile-tile-icon"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                                                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                                                        </svg>
                                                        <span>@{profile.instagram}</span>
                                                    </a>
                                                ) : (
                                                    <button
                                                        className="profile-tile-row interactive"
                                                        onClick={() => navigate('/profile/edit')}
                                                    >
                                                        <svg
                                                            className="profile-tile-icon"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                                                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                                                        </svg>
                                                        <span className="add-text">Add Instagram</span>
                                                    </button>
                                                )}
                                                {/* University Row */}
                                                {getDisplayUniversityName() ? (
                                                     <div className="profile-tile-row university-tile-row">
                                                          <div className="profile-tile-row-content">
                                                              {profile?.university && SUPPORTED_CITIES.some(c => c.label === profile.university) ? (
                                                                  <svg className="profile-tile-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                                      <circle cx="12" cy="10" r="3"></circle>
                                                                  </svg>
                                                              ) : (
                                                                  <svg className="profile-tile-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                                                                      <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"></path>
                                                                  </svg>
                                                              )}
                                                              <span>{getDisplayUniversityName()}</span>
                                                          </div>
                                                     </div>
                                                ) : (
                                                     <button
                                                         className="profile-tile-row interactive"
                                                         onClick={() => navigate('/profile/edit')}
                                                     >
                                                         <svg className="profile-tile-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                             <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                                                             <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"></path>
                                                         </svg>
                                                         <span className="add-text">Set University or City</span>
                                                     </button>
                                                )}

                                                {/* Verification Row */}
                                                {profile?.student_verification_status === 'approved' ? (
                                                    <div className="profile-tile-row verified-student-row">
                                                        <svg className="profile-tile-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <circle cx="12" cy="12" r="10" fill="#10b981" />
                                                            <path d="M8.5 12.5L11 15L16 9.5" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <span>Verified User</span>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="profile-tile-row interactive get-verified-row"
                                                        onClick={() => {
                                                            setSelectedUniversityId(profile?.university_id?.toString() || '');
                                                            setStudentIdUploadError(null);
                                                            setShowStudentVerifyModal(true);
                                                        }}
                                                    >
                                                        <svg className="profile-tile-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="12" cy="12" r="10" />
                                                            <path d="m9 12 2 2 4-4" />
                                                        </svg>
                                                        <span>
                                                            {profile?.student_verification_status === 'pending' ? (
                                                                'Get Verified (Pending)'
                                                            ) : profile?.student_verification_status === 'rejected' ? (
                                                                'Get Verified (Rejected — Re-upload)'
                                                            ) : (
                                                                'Get Verified'
                                                            )}
                                                        </span>
                                                    </button>
                                                )}

                                                {/* Edit Profile Row */}
                                                <button
                                                    className="profile-tile-row interactive edit-profile-row"
                                                    onClick={() => navigate('/profile/edit')}
                                                >
                                                    <svg
                                                        className="profile-tile-icon"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    >
                                                        <path d="M12 20h9"></path>
                                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                                    </svg>
                                                    <span>Edit Profile</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Success/Error Messages */}
                        {success && <div className="success-message">{success}</div>}
                        {error && (
                            <div className="error-message">
                                <div>{error}</div>
                                {lastErrorDetails && (
                                    <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.8, fontFamily: 'monospace' }}>
                                        Details: {lastErrorDetails}
                                    </div>
                                )}
                                <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.8 }}>
                                    Check browser console for additional debugging information
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="profile-listings-card">
                        {/* User Listings and Settings */}
                        <div className="profile-content-section">
                            <div className="listings-subsection">
                                <div className="listings-subsection-header">
                                    <h3>Listings</h3>
                                </div>
                                {listingsLoading ? (
                                    <div className="profile-listings-loading">
                                        <div className="profile-loading-dots" aria-label="Loading listings" role="status">
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                ) : listings.length > 0 ? (
                                    <div className="listings-grid">
                                        {listings.map((listing) => {
                                            const status = (listing.verification_status || '').toString().toLowerCase();
                                            const isPending = status === 'pending';
                                            return (
                                                <div
                                                    key={listing.id}
                                                    className={`listing-card ${isPending ? 'listing-pending' : ''}`}
                                                    onClick={() => navigate(`/listing/${listing.id}`)}
                                                >
                                                    <div className="profile-listing-photo">
                                                        <ImageWithFallback
                                                            src={listing.pictures?.[0] ? (listing.pictures[0].startsWith('http') ? listing.pictures[0] : `${API_BASE_URL}${listing.pictures[0]}`) : undefined}
                                                            alt={listing.title || 'Listing'}
                                                        />
                                                    </div>
                                                    <div className="profile-listing-main">
                                                        <div className="profile-listing-main-top">
                                                            <div className="profile-listing-title-row">
                                                                <h3 className="profile-listing-title">
                                                                    {listing.title}
                                                                    {listing.verification_status === 'rejected' && (
                                                                        <span
                                                                            className={`verification-status-inline rejected ${listing.rejection_reason ? 'rejected-clickable' : ''}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (listing.rejection_reason) {
                                                                                    showRejectionReason(listing.rejection_reason);
                                                                                }
                                                                            }}
                                                                            title={listing.rejection_reason ? 'Click to view rejection reason' : 'No rejection reason provided'}
                                                                        >
                                                                            ✗ Rejected
                                                                        </span>
                                                                    )}
                                                                </h3>
                                                            </div>
                                                        </div>

                                                        {!isPending && (
                                                            <div className="profile-listing-main-bottom">
                                                                <div
                                                                    className="profile-listing-action profile-listing-action--edit"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/listing/${listing.id}/edit`);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            navigate(`/listing/${listing.id}/edit`);
                                                                        }
                                                                    }}
                                                                >
                                                                    EDIT
                                                                </div>
                                                                <div
                                                                    className="profile-listing-action profile-listing-action--delete"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteListing(listing);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleDeleteListing(listing);
                                                                        }
                                                                    }}
                                                                >
                                                                    DELETE
                                                                </div>
                                                                <div
                                                                    className={`profile-listing-action profile-listing-action--boost ${listing.is_boosted ? 'boosted' : ''}`}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (listing.is_boosted) {
                                                                            handleShowBoostTime(listing.id);
                                                                        } else {
                                                                            setBoostListingId(listing.id);
                                                                            setIsBoostModalOpen(true);
                                                                        }
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            if (listing.is_boosted) {
                                                                                handleShowBoostTime(listing.id);
                                                                            } else {
                                                                                setBoostListingId(listing.id);
                                                                                setIsBoostModalOpen(true);
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <span className="fire-text">{listing.is_boosted ? 'BOOSTED' : 'BOOST'}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="profile-empty-listings">
                                        <button className="create-listing-profile-btn" onClick={() => navigate('/create')}>
                                            Create Listing
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            <BoostModal
                isOpen={isBoostModalOpen}
                onClose={() => setIsBoostModalOpen(false)}
                initialListingId={boostListingId}
            />


            {/* Student Verification Modal */}
            {showStudentVerifyModal && (
                <div className="student-verify-modal-overlay" onClick={() => !isUploadingStudentId && setShowStudentVerifyModal(false)}>
                    <div className="student-verify-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="student-verify-modal-header">
                            <div className="student-verify-modal-title">
                                <h2>ID Verification</h2>
                            </div>
                            <button
                                className="student-verify-modal-close"
                                onClick={() => setShowStudentVerifyModal(false)}
                                disabled={isUploadingStudentId}
                            >✕</button>
                        </div>

                        <div className="student-verify-modal-body">
                            {/* Short description */}
                            <p className="student-verify-desc" style={{ marginBottom: '16px' }}>
                                Upload a photo of your student ID or government ID to receive a verified badge. Your photo will be securely deleted after 30 days.
                            </p>

                            {/* Error message */}
                            {studentIdUploadError && (
                                <div className="student-verify-error">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                        <circle cx="12" cy="12" r="10" fill="#ef4444" opacity="0.15" stroke="#ef4444" strokeWidth="1.5"/>
                                        <path d="M12 8v4M12 16h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    {studentIdUploadError}
                                </div>
                            )}

                            {/* Upload Actions */}
                            <div className="student-verify-actions">
                                <button
                                    className="student-verify-cancel-btn"
                                    onClick={() => setShowStudentVerifyModal(false)}
                                    disabled={isUploadingStudentId}
                                >
                                    Cancel
                                </button>
                                {isUploadingStudentId ? (
                                    <button className="student-verify-upload-btn" disabled>
                                        <svg className="student-verify-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" opacity="0.25"/>
                                            <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="white"/>
                                        </svg>
                                        Uploading...
                                    </button>
                                ) : (
                                    <div className="student-verify-photo-actions">
                                        <button
                                            type="button"
                                            className="student-verify-upload-btn"
                                            onClick={() => triggerStudentIdUpload('gallery')}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                <polyline points="21 15 16 10 5 21"></polyline>
                                            </svg>
                                            Choose Photo
                                        </button>
                                        <button
                                            type="button"
                                            className="student-verify-upload-btn student-verify-camera-btn"
                                            onClick={() => triggerStudentIdUpload('camera')}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                                <circle cx="12" cy="13" r="4"></circle>
                                            </svg>
                                            Take Photo
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <div className={`modal-overlay ${isDeleteModalOpen && listingToDelete ? 'modal-overlay-visible' : 'modal-overlay-hidden'}`} onClick={isDeleteModalOpen ? cancelDeleteListing : undefined}>
                <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="delete-modal-body">
                        <h3>Delete Listing</h3>
                        <p className="delete-modal-question">Did you find your sublessee through USublease?</p>
                        <div className="delete-modal-radio-group">
                            <label className="delete-modal-radio-label">
                                <input
                                    type="radio"
                                    name="gotItThroughUsublease"
                                    checked={gotItThroughUsublease === true}
                                    onChange={() => setGotItThroughUsublease(true)}
                                    disabled={isDeletingListing}
                                />
                                <span>Yes</span>
                            </label>
                            <label className="delete-modal-radio-label">
                                <input
                                    type="radio"
                                    name="gotItThroughUsublease"
                                    checked={gotItThroughUsublease === false}
                                    onChange={() => setGotItThroughUsublease(false)}
                                    disabled={isDeletingListing}
                                />
                                <span>No</span>
                            </label>
                        </div>
                        <div className="delete-modal-feedback-group">
                            <label htmlFor="delete-feedback" className="delete-modal-feedback-label">Feedback</label>
                            <textarea
                                id="delete-feedback"
                                className="delete-modal-feedback-textarea"
                                value={deleteFeedback}
                                onChange={(e) => setDeleteFeedback(e.target.value)}
                                placeholder="Tell us why you're deleting this listing..."
                                disabled={isDeletingListing}
                                rows={4}
                            />
                        </div>
                    </div>

                    <div className="delete-modal-actions">
                        <button
                            type="button"
                            onClick={cancelDeleteListing}
                            className="cancel-btn"
                            disabled={isDeletingListing}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirmDeleteListing}
                            className="delete-confirm-btn"
                            disabled={isDeletingListing}
                        >
                            {isDeletingListing ? 'Deleting...' : 'Confirm'}
                        </button>
                    </div>
                </div>
            </div>

            <RejectionReasonModal
                isOpen={showRejectionModal}
                onClose={closeRejectionModal}
                onSubmit={() => { }} // Not used in view-only mode
                title="Rejection Reason"
                rejectionReason={rejectionReason}
                isViewOnly={true}
            />



            <GlassCapsulePopup
                message="Request submitted"
                show={showVerificationSuccess}
                onClose={() => setShowVerificationSuccess(false)}
                autoHideDuration={3000}
            />

            <GlassCapsulePopup
                message="ID verification submitted for review"
                show={showStudentIdSuccess}
                onClose={() => setShowStudentIdSuccess(false)}
                autoHideDuration={4000}
            />

            <GlassCapsulePopup
                message="Avatar updated successfully"
                show={showAvatarSuccess}
                onClose={() => setShowAvatarSuccess(false)}
                autoHideDuration={3000}
            />


            <GlassCapsulePopup
                message="Profile updated successfully"
                show={showProfileUpdateSuccess}
                onClose={() => setShowProfileUpdateSuccess(false)}
                autoHideDuration={3000}
            />

            <GlassCapsulePopup
                message="Listing deleted successfully"
                show={showDeleteSuccess}
                onClose={() => setShowDeleteSuccess(false)}
                autoHideDuration={3000}
            />

            <GlassCapsulePopup
                message="Boost Applied!"
                className="fire-popup"
                show={showBoostSuccess}
                onClose={() => setShowBoostSuccess(false)}
                autoHideDuration={3000}
            />

            <GlassCapsulePopup
                message={boostTimeRemaining || ''}
                className="fire-popup"
                show={showBoostTimePopup}
                onClose={() => setShowBoostTimePopup(false)}
                autoHideDuration={4000}
            />

        </div>
    );
};

export default Profile;
